import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useLocations } from '@/hooks/useLocations';
import { useClearCart } from '@/hooks/useCart';
import { useLocationStore } from '@/stores/useLocationStore';
import { useCartStore } from '@/stores/useCartStore';
import { Button } from '@/components/ui/button';
import { formatLocationName } from '@/lib/bookingDestination';
import type { CustomerLocationDto } from '@/types';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LocationSelectContentProps { onSelect?: () => void }

export const LocationSelectContent = ({ onSelect }: LocationSelectContentProps) => {
    const { location, setLocation } = useLocationStore();
    const { data: locations, isLoading, isError, refetch } = useLocations();
    const { items, isLocked } = useCartStore();
    const clearCart = useClearCart();
    const [pending, setPending] = useState<CustomerLocationDto | null>(null);
    const [error, setError] = useState<string | null>(null);
    const countries = [...new Set((locations ?? []).map(loc => loc.country?.trim() || 'Other pickup locations'))].sort();

    const choose = (next: CustomerLocationDto) => {
        if (items.some(item => item.locationId !== next.id)) {
            setError(null);
            setPending(next);
            return;
        }
        setLocation(next);
        onSelect?.();
    };
    const startNewBooking = async () => {
        if (!pending || isLocked) return;
        try {
            // Wait for the server before changing destination. Failed requests retain the cart.
            await clearCart.mutateAsync();
            setLocation(pending);
            setPending(null);
            onSelect?.();
        } catch {
            setError('We could not start a new booking. Your current destination is unchanged. Please try again.');
        }
    };
    return (
        <>
            <div className="max-h-[65vh] overflow-y-auto p-3">
                <p className="mb-3 text-sm text-foreground">Choose where you need the equipment, not where you live. Travelling from Dublin to Alicante? Choose Alicante.</p>
                {isLoading && <p className="py-4 text-sm">Loading pickup locations…</p>}
                {isError && <Button variant="outline" onClick={() => void refetch()}>Retry pickup locations</Button>}
                {countries.map(country => (
                    <section key={country} className="mb-3">
                        <h3 className="px-2 py-2 text-sm font-bold text-foreground">{country}</h3>
                        {(locations ?? []).filter(loc => (loc.country?.trim() || 'Other pickup locations') === country).map(loc => (
                            <button type="button" key={loc.id} onClick={() => choose(loc)}
                                className={cn('flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', location?.id === loc.id && 'bg-primary/10 text-primary')}
                                aria-pressed={location?.id === loc.id}>
                                <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                                <span className="break-words font-semibold">{formatLocationName(loc)}</span>
                            </button>
                        ))}
                    </section>
                ))}
            </div>
            <Dialog open={!!pending} onClose={() => { if (!clearCart.isPending) setPending(null); }} className="relative z-[250]">
                <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
                    <Dialog.Panel className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
                        <Dialog.Title className="text-xl font-bold">Start a booking in {pending && formatLocationName(pending)}?</Dialog.Title>
                        <Dialog.Description className="mt-3 text-sm text-foreground">
                            {isLocked ? 'This reservation has a fixed pickup destination. Contact us to change it.' : 'Your current items will be removed. You will choose equipment available in the new destination. Your dates and contact details will be kept.'}
                        </Dialog.Description>
                        <ul className="my-4 space-y-2 text-sm">
                            {items.map(item => <li key={item.id}><strong>{item.productName}</strong> — {item.locationName || 'Pickup destination needs review'}</li>)}
                        </ul>
                        {error && <p role="alert" className="mb-3 text-sm text-destructive">{error}</p>}
                        <div className="flex flex-col gap-2">
                            <Button variant="outline" disabled={clearCart.isPending} onClick={() => setPending(null)}>Keep current booking</Button>
                            {!isLocked && <Button disabled={clearCart.isPending} onClick={() => void startNewBooking()}>{clearCart.isPending ? 'Starting…' : 'Remove items & start new booking'}</Button>}
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </>
    );
};
